import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, List } from 'antd';
import React, { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import useWaitingApply from '../../hooks/useWaitingApply';
import InputJsonDataRow, { EffectListType, JsonParamDataRowType, JsonParamDataType } from './InputJsonDataRow';

const { TextArea } = Input;

const DELAY_UPDATING_SUBTASK = 1000;

interface IInputJsonData<T> {
    disabled?: boolean;
    fieldJsonParam: JsonParamDataType;
    value?: string;
    onChange?: (value: string) => void;
    effectList?: EffectListType<T>;
    isAccess?: { create: boolean; update: boolean; delete: boolean };
}

const InputJsonData = React.forwardRef(
    <T extends Record<string, any>>(
        { disabled, fieldJsonParam, onChange, isAccess, value, effectList, ...restProps }: IInputJsonData<T>,
        _ref: React.Ref<any>,
    ) => {
        const [jsonData, setJsonData] = React.useState<T[]>([]);

        useEffect(() => {
            if (value && value !== JSON.stringify(jsonData)) {
                setTimeout(() => {
                    setJsonData(JSON.parse(value));
                }, 100);
            }
        }, [value]);

        const callbackWaitingApply = () => {
            if (onChange) {
                onChange(JSON.stringify(jsonData));
            }
        };

        useWaitingApply(jsonData, callbackWaitingApply, DELAY_UPDATING_SUBTASK);

        const getDefaultValue = (field: JsonParamDataRowType) => {
            switch (field.type) {
                case 'checkbox':
                    return field.defaultValue || false;
                case 'number':
                    return field.defaultValue || 0;
                case 'string':
                    return field.defaultValue || '';
                default:
                    return '';
            }
        };

        const onClickAdd = () => {
            const newJsonDataElement: { [key: string]: any } = {};

            newJsonDataElement.id = uuidv4();
            for (const keyField of Object.keys(fieldJsonParam)) {
                newJsonDataElement[keyField] = getDefaultValue(fieldJsonParam[keyField]);
            }

            setJsonData([...jsonData, newJsonDataElement as T]);
        };

        return (
            <>
                <TextArea
                    autoSize={{ minRows: 3, maxRows: 5 }}
                    {...restProps}
                    // onChange={onChange}
                    value={value}
                    style={{ display: 'none' }}
                />

                <Button
                    color="default"
                    variant="text"
                    icon={<PlusOutlined />}
                    onClick={onClickAdd}
                    size={'small'}
                    style={{ marginTop: 5, marginBottom: 5 }}
                    disabled={disabled || !isAccess?.create}
                >
                    Add subtask
                </Button>

                <List
                    bordered
                    size={'small'}
                    dataSource={jsonData}
                    renderItem={(item) => (
                        <List.Item>
                            <InputJsonDataRow
                                rowData={item}
                                fieldJsonParam={fieldJsonParam}
                                onChange={(rowData: any, deleteId?: string) => {
                                    if (deleteId) {
                                        setJsonData(jsonData.filter((oldRowData) => oldRowData.id !== deleteId));
                                    } else {
                                        setJsonData(
                                            jsonData.map((oldRowData) =>
                                                oldRowData.id === rowData.id ? rowData : oldRowData,
                                            ),
                                        );
                                    }
                                }}
                                effectList={effectList}
                                isAccessCreate={isAccess?.create}
                                isAccessUpdate={isAccess?.update}
                                isAccessDelete={isAccess?.delete}
                                disabled={disabled}
                            />
                        </List.Item>
                    )}
                />
            </>
        );
    },
) as <T extends Record<string, any>>(props: IInputJsonData<T> & { ref?: React.Ref<any> }) => React.JSX.Element;

export default InputJsonData;
